import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import InfoBar from '../../components/Navigation/InfoBar';
import useApi from '../../hooks/use-api';
import { useToast } from '../../hooks/use-toast';
import {
	dationInstanceUserList,
	downloadDrivingSchoolLogo,
	EDIT_USER,
	getDationInstance,
	inviteDationInstanceUserToPortal,
	updateDationInstance,
	updateDationInstanceUser,
	uploadDrivingSchoolLogo,
} from '../../constants/routes';
import useMergeState from '../../hooks/use-merge-state';
import FormCol from '../../components/Form/FormCol';
import { parseErrorMessage } from '../../utils/parseErrors';
import ErrorMessage from '../../components/common/messages/ErrorMessage';
import File from '../../components/Form/File';
import { getFileExtension } from '../../constants/file';
import DationInstanceUserOverview from '../../components/Tables/Users/DationInstanceUser/DationInstanceUserOverview';
import InviteUserModal from '../../components/Modal/InviteUserModal';
import DeleteUserModal from '../DeleteUserModal';
import { getDisplayName } from '../../utils/person';

function DationInstanceDetailPage() {
	const { dationInstanceId } = useParams();
	const { t } = useTranslation();
	const toast = useToast();
	const navigate = useNavigate();

	const initialState = {
		name: undefined,
		apiKey: undefined,
		emailAddress: undefined,
		administrationId: undefined,
		regionName: undefined,
		logoStorageBucketIdentifier: undefined,
	};

	const [showInviteModal, setShowInviteModal] = useState(false);
	const [showDeleteModal, setDeleteModal] = useState(false);
	const [selectedUser, setSelectedUser] = useState(null);

	const [state, mergeState] = useMergeState(initialState);
	const [isValidated, setValidated] = useState(false);
	const [isDisabled, setDisabled] = useState(false);
	const [errorMessage, setErrorMessage] = useState(null);

	const [logoFileData, setLogoFileData] = useState(undefined);

	// Getting extended fields for dationInstanceEndpoint()
	const [{ data }, refetchInstance] = useApi.get(
		`${getDationInstance(dationInstanceId)}`,
		{ expand: ['portal_admin'] },
		{ cachePolicy: 'no-cache' },
	);
	const [{ data: dationInstanceUsers }, fetchDationInstanceUsers] = useApi.get(
		dationInstanceUserList(dationInstanceId),
	);
	const [, submitDationInstance] = useApi.patch(updateDationInstance(dationInstanceId));

	const [, submitLogo] = useApi.post(uploadDrivingSchoolLogo(dationInstanceId));
	const [{ data: downloadedDocument }, downloadDocument] = useApi.get(
		downloadDrivingSchoolLogo(dationInstanceId),
		{},
		{ cachePolicy: 'no-cache', lazy: true, requestConfig: { responseType: 'blob' } },
	);
	const [, deleteLogo] = useApi.delete(
		uploadDrivingSchoolLogo(dationInstanceId),
		{},
		{ lazy: true },
	);

	const submitDrivingSchoolLogo = () => {
		submitLogo({
			fileData: logoFileData,
		})
			.then(() => {
				toast.addToast(t('dation_instance.updated'));
				refetchInstance();
			})
			.catch((e) => {
				setErrorMessage(parseErrorMessage(e));
			});
	};

	const submit = (event) => {
		event.preventDefault();
		event.stopPropagation();

		setDisabled(true);

		const form = event.currentTarget;

		if (form.checkValidity() !== false) {
			submitDationInstance(state)
				.then(() => {
					toast.addToast(t('dation_instance.updated'));
				})
				.catch((e) => {
					setErrorMessage(parseErrorMessage(e));
				})
				.finally(() => setDisabled(false));
		} else {
			setValidated(true);
			setDisabled(false);
		}
	};

	useEffect(() => {
		if (data) {
			mergeState(data);
		}
	}, [data, mergeState]);

	const handleChange = (event) => {
		const { target } = event;
		let value = target.type === 'checkbox' ? target.checked : target.value;

		value = target.type === 'number' ? Number(value) : target.value;

		mergeState({ [event.target.name]: value });
	};
	const onFileChange = (fileChangeResponse) => {
		fileChangeResponse.catch(({ error }) => {
			setErrorMessage(error);
		});
	};

	const handleFileChange = async ({ file, base64File }) => {
		setErrorMessage(null);
		if (file) {
			setLogoFileData(base64File);
		}
	};

	const handleDeleteLogo = () => {
		deleteLogo()
			.then(() => {
				toast.addToast('Logo verwijderd');
				setLogoFileData(null);
				mergeState({ logoStorageBucketIdentifier: null });
			})
			.catch(() => {
				setErrorMessage(t('documents.delete_error'));
			});
	};

	useEffect(() => {
		if (downloadedDocument) {
			const downloadUrl = URL.createObjectURL(downloadedDocument);

			const a = document.createElement('a');
			a.href = downloadUrl;
			a.download = `Logo${getFileExtension(downloadedDocument.type)}`;
			document.body.appendChild(a);
			a.click();
		}
	}, [downloadedDocument]);

	return (
		<>
			<InfoBar
				backTitle={t('navigation.adminUser.dation_instances')}
				badgeTitle={t('company.archived')}
				showBadge={state.archived}
			/>
			<div className="row justify-content-center">
				<div className="col-12 col-xl-5">
					<Card className="mb-3">
						<Card.Body>
							<Form className="company-form" noValidate validated={isValidated} onSubmit={submit}>
								<div className="d-flex justify-content-between">
									<h5>{t('company.general_data')}</h5>
									<Button
										variant="primary"
										size="sm"
										disabled={isDisabled}
										type="submit"
										className="ms-1">
										{t('dation_instance.update')}
									</Button>
								</div>
								<ErrorMessage message={errorMessage} />
								<Row className="mb-3">
									<FormCol
										label={t('dation_instance.name')}
										name="name"
										value={state.name}
										onChange={handleChange}
									/>
									<FormCol
										type="text"
										label={t('dation_instance.api_id')}
										name="apiId"
										value={state.apiId}
										onChange={handleChange}
										feedback={t('add_employee.field_required')}
										required
									/>
								</Row>
								<Row className="mb-3">
									<FormCol
										label={t('dation_instance.email_address')}
										type="email"
										name="emailAddress"
										value={state.emailAddress}
										onChange={handleChange}
										feedback={t('portal.email_not_valid')}
										required
									/>
									<FormCol
										type="number"
										label={t('dation_instance.administration_id')}
										name="administrationId"
										value={state.administrationId}
										onChange={handleChange}
										feedback={t('add_employee.field_required')}
										required
									/>
								</Row>
								<Row className="mb-3">
									<FormCol
										label={t('dation_instance.region_name')}
										type="text"
										name="regionName"
										value={state.regionName}
										onChange={handleChange}
										feedback={t('add_employee.field_required')}
										required
									/>
									<FormCol
										label={t('dation_instance.dation_id')}
										type="number"
										name="dationId"
										value={state.dationId}
										onChange={handleChange}
										feedback={t('add_employee.field_required')}
										required
									/>
								</Row>

								<Row>
									<FormCol
										label={t('dation_instance.handle')}
										type="text"
										name="handle"
										value={state.handle}
										onChange={handleChange}
										feedback={t('add_employee.field_required')}
										required
									/>
									<FormCol
										label={t('dation_instance.phone_number')}
										type="text"
										name="phoneNumber"
										value={state.phoneNumber}
										onChange={handleChange}
									/>
								</Row>
							</Form>
						</Card.Body>
					</Card>
					<Card>
						<Card.Body>
							<div className="d-flex justify-content-between">
								<h5>{t('company.logo')}</h5>
							</div>
							<Row className="mb-12">
								<Col xs={12} md={12}>
									{!state.logoStorageBucketIdentifier && (
										<File
											showFileName={false}
											onChange={onFileChange}
											handleFileChange={handleFileChange}
											customLabel="Selecteer een .JPEG bestand"
										/>
									)}
									{state.logoStorageBucketIdentifier && (
										<div className="btn-group">
											<Button
												type="button"
												variant="outline-secondary"
												onClick={downloadDocument}
												className="d-flex align-items-center">
												<span className="glyphicons glyphicons-cloud-download me-1 text-center d-inline-block p-0 clickable" />
												{t('documents.download')}
											</Button>
											<Button variant="outline-secondary" type="button" onClick={handleDeleteLogo}>
												{t('modalActions.delete')}
											</Button>
										</div>
									)}
								</Col>
							</Row>
							{!state.logoStorageBucketIdentifier && logoFileData && (
								<div className="d-flex justify-content-end">
									<Button variant="primary" onClick={submitDrivingSchoolLogo}>
										Logo opslaan
									</Button>
								</div>
							)}
						</Card.Body>
					</Card>
				</div>
				<div className="col-12 col-xl-7">
					<Card>
						<Card.Body>
							<div className="justify-content-between d-flex">
								<h5>{t('dation_instance_user.add')}</h5>
								<button
									id="add-dation-instance-user"
									type="button"
									className="btn btn-outline pt-0"
									onClick={() =>
										navigate(EDIT_USER, {
											state: {
												userType: 'dation_instance_user',
												original: {},
												dationInstance: { id: data?.id },
											},
										})
									}>
									<span className="glyphicons glyphicons-user-plus font-size-large text-secondary" />
								</button>
							</div>
							<DationInstanceUserOverview
								users={dationInstanceUsers || []}
								handleDelete={(original) => {
									setSelectedUser(original);
									setDeleteModal(true);
								}}
								handleRowClick={(original) =>
									navigate(EDIT_USER, {
										state: {
											userType: 'dation_instance_user',
											original,
											company: undefined,
											dationInstance: original.dationInstance,
										},
									})
								}
								handleInvite={(original) => {
									setSelectedUser(original);
									setShowInviteModal(true);
								}}
							/>

							<InviteUserModal
								show={showInviteModal}
								setShow={setShowInviteModal}
								endpoint={inviteDationInstanceUserToPortal(selectedUser?.id)}
								handleSubmit={fetchDationInstanceUsers}
								title={t('users.send_invite')}
								message={t('users.confirm_invite', { emailAddress: selectedUser?.emailAddress })}
								successMessage={t('users.invite_send', {
									userType: t(`users.type.dation_instance_user`),
									name: getDisplayName(selectedUser || {}),
								})}
							/>
							<DeleteUserModal
								deleteEndpoint={updateDationInstanceUser(selectedUser?.id)}
								show={showDeleteModal}
								setShow={(show) => {
									setSelectedUser({});
									setDeleteModal(show);
								}}
								title={t('users.delete')}
								message={t('users.confirm_delete', {
									userType: t(`users.type.dation_instance_user`),
									name: getDisplayName(selectedUser || {}),
								})}
								handleSubmit={fetchDationInstanceUsers}
								successMessage={t('users.deleted', {
									userType: t(`users.type.dation_instance_user`),
									name: getDisplayName(selectedUser || {}),
								})}
							/>
						</Card.Body>
					</Card>
				</div>
			</div>
		</>
	);
}

export default DationInstanceDetailPage;
